/**
 * Entity Type Definitions
 * 
 * Type-level safety for active vs archived entities.
 * Ensures archived entities cannot flow into selectors at compile time.
 */

import type { Vehicle } from "@/features/vehicles/vehicle.schema";
import type { Customer } from "@/features/customers/customer.schema";
import type { WorkOrder } from "@/features/garage/types";

/**
 * Generic helper type for active (non-archived) entities
 * 
 * An active entity is one where deletedAt is null or undefined.
 * This type ensures that only active entities can be used in contexts
 * where archived entities should not appear (e.g., dropdowns, selectors).
 * 
 * @example
 * type ActiveVehicle = ActiveEntity<Vehicle>;
 * // ActiveVehicle has deletedAt?: null | undefined (never a timestamp)
 */
export type ActiveEntity<T extends { deletedAt?: string | Date | null | undefined }> =
  T & { deletedAt?: null | undefined };

/**
 * Generic type guard to check if an entity is active
 * 
 * This function both:
 * 1. Checks at runtime if deletedAt is falsy
 * 2. Narrows the TypeScript type to ActiveEntity<T>
 * 
 * @example
 * const vehicles: Vehicle[] = [...];
 * const activeVehicles = vehicles.filter(isActive);
 * // activeVehicles is typed as ActiveEntity<Vehicle>[]
 */
export function isActive<T extends { deletedAt?: string | Date | null | undefined }>(
  entity: T
): entity is ActiveEntity<T> {
  return !entity.deletedAt;
}

/**
 * Concrete type aliases for active entities
 */

/**
 * Active Vehicle - guaranteed to not be archived
 * Use this type in selectors, dropdowns, and new assignments
 */
export type ActiveVehicle = ActiveEntity<Vehicle>;

/**
 * Active Customer - guaranteed to not be archived
 * Use this type in selectors, dropdowns, and new assignments
 */
export type ActiveCustomer = ActiveEntity<Customer>;

/**
 * Active Work Order - guaranteed to not be archived
 * Use this type in selectors, dropdowns, and new assignments
 */
export type ActiveWorkOrder = ActiveEntity<WorkOrder>;

/**
 * Active Invoice - guaranteed to not be archived
 * (Generic type - can be used for both sales and purchase invoices)
 */
export type ActiveInvoice<T extends { deletedAt?: string | null | undefined }> = ActiveEntity<T>;

/**
 * Team Member Active Type
 * 
 * Team members use 'active' field instead of 'deletedAt'
 */
export type ActiveTeamMember<T extends { active?: boolean }> =
  T & { active: true };

/**
 * Type guard for active team members
 */
export function isTeamMemberActive<T extends { active?: boolean }>(
  member: T
): member is ActiveTeamMember<T> {
  return member.active !== false;
}
